import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Tabs, Tab } from "react-bootstrap";
import { networkService } from "../../config/axios";
import ColumnChart from "../Stats/Charts/ColumnChart";
import ShowCharts from "../Stats/Charts/ShowCharts";
import SelectChart from "../Stats/Charts/SelectChart";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";

const RecipientGlancePage = () => {
    const { id: recipientId } = useParams();
    const navigate = useNavigate();
    const [recipient, setRecipient] = useState(null);
    const [chartsData, setChartsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [threshold, setThreshold] = useState(20);
    const [editingThreshold, setEditingThreshold] = useState(false);
    const [selectedChart, setSelectedChart] = useState({
        browser: 'Pie',
        os: 'Pie',
        device: 'Pie',
        city: 'Pie',
        region: 'Pie',
    });
    const mountedRef = useRef(true);

    // Fetch recipient data — only when recipientId changes
    useEffect(() => {
        mountedRef.current = true;
        setLoading(true);

        networkService(`/show?id=${recipientId}`, "GET")
            .then((response) => {
                if (mountedRef.current && response?.data?.recipient) {
                    setRecipient(response.data.recipient);
                }
            })
            .catch((error) => {
                if (mountedRef.current) console.error("Error fetching recipient:", error);
            })
            .finally(() => {
                if (mountedRef.current) setLoading(false);
            });

        return () => { mountedRef.current = false; };
    }, [recipientId]);

    // Fetch charts data — when recipientId or threshold changes
    useEffect(() => {
        mountedRef.current = true;

        networkService("/charts_data", "POST", {
            id: recipientId,
            threshold: threshold,
        })
            .then((response) => {
                if (mountedRef.current && response?.data) {
                    setChartsData(response.data.charts_data);
                }
            })
            .catch((error) => {
                if (mountedRef.current) console.error("Error fetching charts data:", error);
            });

        return () => { mountedRef.current = false; };
    }, [recipientId, threshold]);

    const handleThresholdChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 0) {
            setThreshold(value);
        }
    };

    const handleSelectChange = ({ target: { value } }, type) => {
        setSelectedChart((prevState) => ({
            ...prevState,
            [type]: value,
        }));
    };

    if (loading) {
        return <LoadingScreen />;
    }

    if (!recipient) {
        return (
            <div className="header-content-main">
                <div className="row">
                    <div className="col-10">
                        <p>Recipient not found.</p>
                    </div>

                    <div className="col-2">
                        <button className="btn btn-info" onClick={() => navigate("/recipients")}>
                            Back to Recipients
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="header-content-main">

            <div className="row">
                        <div className="col-10">
                        <h5
                            className="text-truncate"
                            title={`${recipient.first_name || ""} ${recipient.last_name || ""} ${recipient.email ? `(${recipient.email})` : ""}`.trim()}
                        >
                    {recipient.first_name} {recipient.last_name}
                    {recipient.email ? ` (${recipient.email})` : ""}
                </h5>
                        </div>
                        <div className="col-2">
                        <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate("/recipients")}
                    >
                        &larr; Back to Recipients
                    </button>
                        </div>
                    </div>

            {/* Threshold Filter */}
            <section className="mb-3">
                <span>
                    <span>Engagement Threshold:</span>&nbsp;
                    {editingThreshold ? (
                        <input
                            type="number"
                            value={threshold}
                            onChange={handleThresholdChange}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingThreshold(false);
                            }}
                            onBlur={() => setEditingThreshold(false)}
                            className="form-control d-inline-block"
                            style={{ width: "60px" }}
                            autoFocus
                        />
                    ) : (
                        <span
                            className="cursor-pointer"
                            style={{ fontWeight: "bold", textDecoration: "underline" }}
                            onClick={() => setEditingThreshold(true)}
                        >
                            {threshold}
                        </span>
                    )}
                    &nbsp;Seconds
                </span>
            </section>

            {/* At a Glance Content */}
            <div className="laserlink-stats-container">
                <Row>
                    <Col md={12}>
                        <h4>Overview</h4>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Card className="p-3 h-100 d-flex">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <h5>Performance</h5>
                                        <p>
                                            <strong>{recipient.sessions?.length || 0}</strong>
                                            <br /> Views
                                        </p>
                                        <p>
                                            <strong>{recipient.session_pages?.length || 0}</strong>
                                            <br /> Pages Visited
                                        </p>
                                        <p>
                                            <strong    className="d-block text-truncate"
                                                title={recipient.most_visited_kdf?.title || "N/A"}>{recipient.top_visited_kdfs || "N/A"}</strong>
                                            <br /> Top Viewed KDF
                                        </p>
                                        <p>
                                            <strong
                                                className="d-block text-truncate"
                                                title={recipient.most_visited_kdf?.title || "N/A"}
                                            >
                                                {recipient.most_visited_kdf?.title || "N/A"}
                                            </strong>
                                            <br /> Most Visited KDF
                                        </p>
                                    </Col>
                                    <Col md={6}>
                                        <h5>Behaviour</h5>
                                        {recipient.most_time_spent ? (
                                            <div className="mb-3">
                                                <strong>Most Time Spent</strong>
                                                <br />
                                                <span
                                                    className="d-block text-truncate"
                                                    title={recipient.most_time_spent.title}
                                                >
                                                    Title: {recipient.most_time_spent.title}
                                                </span>
                                                <br />
                                                <span>Page: {recipient.most_time_spent.page}</span>
                                                <br />
                                                <span>Time: {recipient.most_time_spent.time} secs</span>
                                            </div>
                                        ) : (
                                            <p>
                                                <strong>N/A</strong>
                                                <br /> Most Time Spent
                                            </p>
                                        )}
                                        <p>
                                            <strong>
                                                {recipient.video_elements_clicked || 0}/
                                                {recipient.video_elements || 0}
                                            </strong>
                                            <br /> Viewed Video
                                        </p>
                                        <p>
                                            <strong>{recipient.engagement || "0%"}</strong>
                                            <br /> Engagement
                                        </p>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Tabs
                            defaultActiveKey="mostVisitedPage"
                            className="select-file-pop stats-nav"
                        >
                            <Tab eventKey="mostVisitedPage" title="Most Visited Pages">
                                <div>
                                    {chartsData?.most_visited_pages?.length > 0 ? (
                                        <ColumnChart
                                            data={chartsData.most_visited_pages}
                                            name="page_views"
                                            xAxisLabel="Pages"
                                            yAxisLabel="Total visits"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="timePerPage" title="Time per Page">
                                <div>
                                    {chartsData?.time_per_page?.length > 0 ? (
                                        <ColumnChart
                                            data={chartsData.time_per_page}
                                            name="total_secs_on_page"
                                            xAxisLabel="Pages"
                                            yAxisLabel="Total duration (sec)"
                                            formatTooltipAsDuration
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Tabs
                            defaultActiveKey="browser"
                            className="mt-4 select-file-pop stats-nav"
                        >
                            <Tab eventKey="browser" title="Browser">
                                <div>
                                    <SelectChart
                                        selectedChart={selectedChart}
                                        handleSelectChange={handleSelectChange}
                                        name="browser"
                                    />
                                    {chartsData?.by_browser?.length > 0 ? (
                                        <ShowCharts
                                            selectedChart={selectedChart["browser"]}
                                            data={chartsData.by_browser}
                                            name="browser"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="os" title="OS">
                                <div>
                                    <SelectChart
                                        selectedChart={selectedChart}
                                        handleSelectChange={handleSelectChange}
                                        name="os"
                                    />
                                    {chartsData?.by_os?.length > 0 ? (
                                        <ShowCharts
                                            selectedChart={selectedChart["os"]}
                                            data={chartsData.by_os}
                                            name="os_family"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="device" title="Device">
                                <div>
                                    <SelectChart
                                        selectedChart={selectedChart}
                                        handleSelectChange={handleSelectChange}
                                        name="device"
                                    />
                                    {chartsData?.by_device?.length > 0 ? (
                                        <ShowCharts
                                            selectedChart={selectedChart["device"]}
                                            data={chartsData.by_device}
                                            name="device"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                    </Col>
                    <Col md={6}>
                        <Tabs
                            defaultActiveKey="city"
                            className="mt-4 select-file-pop cursor-pointer stats-nav"
                        >
                            <Tab eventKey="city" title="City">
                                <div>
                                    <SelectChart
                                        selectedChart={selectedChart}
                                        handleSelectChange={handleSelectChange}
                                        name="city"
                                    />
                                    {chartsData?.by_city?.length > 0 ? (
                                        <ShowCharts
                                            selectedChart={selectedChart["city"]}
                                            data={chartsData.by_city}
                                            name="city"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="region" title="Region">
                                <div>
                                    <SelectChart
                                        selectedChart={selectedChart}
                                        handleSelectChange={handleSelectChange}
                                        name="region"
                                    />
                                    {chartsData?.by_subdivision?.length > 0 ? (
                                        <ShowCharts
                                            selectedChart={selectedChart["region"]}
                                            data={chartsData.by_subdivision}
                                            name="subdivision"
                                        />
                                    ) : (
                                        <p className="text-muted mt-3">No data available</p>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default RecipientGlancePage;
